import React, { useCallback, useState } from 'react';
import { NotFound } from 'src/components/common';
import { useNavigate } from 'react-router-dom';
import { ResourceItemComponent } from './resource-item.component';
import { KNOWLEDGE_RESOURCES_TYPES } from 'shared/constants';
import { ListItemsNavigator } from 'src/components/common/items-navigator/list';
import styles from './resource-manager.module.scss';

const defaultRenderItemOptions = {
  showContentType: true,
  showTags: true,
  showLabels: true,
  showMetadata: true,
};

const ResourceList = ({
  resources = [],
  canEdit = false,
  disabled = false,
  disabledHint = '',
  doRemove = () => {},
  analyticsTriggers = {},
  notFoundText = '',
  wrapperClass = '',
  listClassName = '',
  showNotFoundPanda = false,
  openInNewTab = false,
  renderItemOptions = defaultRenderItemOptions,
  showTooltipRemoveConfirmation = true,
}) => {
  const [loading, setLoading] = useState(null);

  const handleRemove = useCallback(
    async (e, { type, url, id }) => {
      if (loading) return;
      e.stopPropagation();
      e.preventDefault();

      setLoading(id);

      // if the attachment is a external url, find it by url, otherwise find it by id
      const itemToRemove = resources.find(item =>
        type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL &&
        item.type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL
          ? item.url === url
          : item.id === id,
      );
      await doRemove(itemToRemove);
      analyticsTriggers?.remove?.(itemToRemove);

      setLoading(null);
    },
    [analyticsTriggers, doRemove, loading, resources],
  );

  const navigate = useNavigate();

  const onClick = useCallback(
    (navigateTo, item) => {
      analyticsTriggers?.open?.(item);
      if (openInNewTab) {
        window.open(navigateTo, '_blank').focus();
        return;
      }
      navigate(navigateTo);
    },
    [navigate, analyticsTriggers, openInNewTab],
  );

  const onOpenExternalUrl = useCallback(
    (navigateTo, item) => {
      analyticsTriggers?.open?.(item);
    },
    [analyticsTriggers],
  );

  if (!resources.length) {
    return showNotFoundPanda ? (
      <NotFound text={notFoundText} />
    ) : (
      <p className={styles.emptyText}>{notFoundText}</p>
    );
  }

  return (
    <ListItemsNavigator className={listClassName}>
      {resources.map((item, index) => {
        const metadata = item.metadata ?? {
          items: item.checks?.length || item.values?.length || 0,
        };

        return (
          <ResourceItemComponent
            key={index}
            parsedResource={{ ...item, metadata }}
            canEdit={canEdit}
            disabled={disabled}
            disabledHint={disabledHint}
            loading={loading}
            handleRemove={handleRemove}
            onOpenExternalUrl={onOpenExternalUrl}
            onClick={onClick}
            wrapperClass={wrapperClass}
            renderItemOptions={renderItemOptions}
            showTooltipRemoveConfirmation={showTooltipRemoveConfirmation}
          />
        );
      })}
    </ListItemsNavigator>
  );
};

export { ResourceList };
